import { useCallback } from "react";

import { Box, BoxProps, Button, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router";

import { HeroText } from "components/shared/Typography";
import { colors } from "theme";
import { Workflow } from "types/design";

interface TargetItemProps {
  type: Workflow;
  title: string;
  description: string;
  buttonText: string;
  color: any;
  mark: string;
}

const TargetItems: TargetItemProps[] = [
  {
    type: Workflow.FUSION,
    title: "fusion",
    description: "RNA-based fusion, expression and variant detection.",
    buttonText: "Browse FusionPlex panels",
    color: "secondary",
    mark: "®",
  },
  {
    type: Workflow.VARITANT,
    title: "variant",
    description: "DNA-based variant, CNV, and genomic signatures detection.",
    buttonText: "Browse VariantPlex panels",
    color: "primary",
    mark: "®",
  },
  {
    type: Workflow.LIQUID,
    title: "liquid",
    description: "Sensitive variant detection from liquid biopsies.",
    buttonText: "Browse LiquidPlex panels",
    color: "error",
    mark: "™",
  },
];

export const TargetList = () => {
  const history = useHistory();

  const handleClick = useCallback(
    (type) => (event: React.MouseEvent<HTMLElement>) => {
      const redirectUrl = `/catalog?workflows=${type}`;
      event.ctrlKey
        ? window.open(redirectUrl, "_blank")
        : history.push(redirectUrl);
    },
    [history]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      rowGap={{ xs: "18px", sm: 3, md: 4 }}
      maxWidth={{ xs: 428, md: "unset" }}
      margin="auto"
    >
      <HeroText align="center" component="h2">
        Next-Generation Targeted Sequencing
      </HeroText>
      <Grid container spacing={3} justifyContent="center">
        {TargetItems.map(
          ({ color, type, title, description, mark, buttonText }) => (
            <Grid item xs={12} md={4} key={type}>
              <Box
                boxShadow={1}
                bgcolor={colors.white}
                borderColor={colors.grey[500]}
                padding={3.75}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                height={207}
                boxSizing="border-box"
              >
                <Box display="flex" height={20}>
                  <Typography component="div">
                    <WorkflowTitle fontWeight={700} color={`${color}.main`}>
                      {title}
                    </WorkflowTitle>
                  </Typography>
                  <Typography component="div">
                    <Box
                      component="span"
                      fontSize={24}
                      lineHeight="21px"
                      color={colors.grey[200]}
                    >
                      plex
                    </Box>
                    <Box component="sup" fontSize={12} color={colors.grey[200]}>
                      {mark}
                    </Box>
                  </Typography>
                </Box>
                <Typography variant="subtitle1">
                  <Box component="span" display="inline-block">
                    {description}
                  </Box>
                </Typography>
                <Button fullWidth onClick={handleClick(type)} color={color}>
                  {buttonText}
                </Button>
              </Box>
            </Grid>
          )
        )}
      </Grid>
    </Box>
  );
};

const WorkflowTitle = styled((props: BoxProps) => (
  <Box component="span" {...props} />
))({
  fontSize: 24,
  lineHeight: "21px",
  textTransform: "uppercase",
});
